import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import DynamicBreadcrumb from "../header/breadcrumb";
import offerAPI from "../../services/offerapi";
import branchApi from "../../services/branchapi";
import ProductItemSearchSelect from "../../components/forms/ProductItemSearchSelect";

const createEmptyRow = () => ({
  offer_item_id: "",
  free_item_id: "",
  offer_quantity: 1,
  free_quantity: 1,
  max_qty: 0,
});

const OfferManualForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [branches, setBranches] = useState([]);

  const [form, setForm] = useState({
    offer_name: "",
    offer_type: 4,
    offer_start_date: "",
    offer_end_date: "",
    status: 1,
    branch_ids: [],
    products: [createEmptyRow()],
  });

  const allBranchIds = useMemo(
    () => branches.map((branch) => Number(branch.branchId)),
    [branches]
  );

  const isAllBranchesSelected = useMemo(() => {
    if (!allBranchIds.length) return false;
    return allBranchIds.every((idValue) => form.branch_ids.includes(idValue));
  }, [allBranchIds, form.branch_ids]);

  const breadcrumb = useMemo(
    () => [
      { name: "BOGO Offers List", url: "/admin/offers/bogo" },
      { name: isEdit ? "Edit BOGO Offer" : "Create BOGO Offer", url: null },
    ],
    [isEdit]
  );

  const loadDropdowns = async () => {
    const branchResp = await branchApi.getAllBranches({
      limit: 500,
      page: 0,
      status: 1,
      sortModel: [{ field: "branchId", sort: "asc" }],
      filterModel: {},
    });

    setBranches(branchResp?.data?.result || []);
  };
  const loadOffer = async () => {
    if (!isEdit) return;

    const resp = await offerAPI.getManualOffer(id);
    const offer = resp?.data;

    if (!offer) {
      throw new Error("Offer data not found");
    }

    setForm({
      offer_name: offer.offer_name || "",
      offer_type: Number(offer.offer_type || 4),
      offer_start_date: offer.offer_start_date ? String(offer.offer_start_date).slice(0, 10) : "",
      offer_end_date: offer.offer_end_date ? String(offer.offer_end_date).slice(0, 10) : "",
      status: Number(offer.status || 1),
      branch_ids: (offer.branch_ids || []).map(Number),
      products:
        (offer.products || []).length > 0
          ? offer.products.map((row) => ({
            offer_item_id: Number(row.offer_item_id || ""),
            free_item_id: Number(row.free_item_id || ""),
            offer_quantity: Number(row.offer_quantity || 1),
            free_quantity: Number(row.free_quantity || 1),
            max_qty: Number(row.max_qty || 0),
          }))
          : [createEmptyRow()],
    });
  };

  useEffect(() => {
    const run = async () => {
      try {
        setError("");
        await loadDropdowns();
        await loadOffer();
      } catch (err) {
        setError(err?.response?.data?.message || err.message || "Failed to load form");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [id]);

  const handleFieldChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleBranchChange = (branchId, checked) => {
    const numericId = Number(branchId);
    setForm((prev) => ({
      ...prev,
      branch_ids: checked
        ? [...new Set([...prev.branch_ids, numericId])]
        : prev.branch_ids.filter((idValue) => Number(idValue) !== numericId),
    }));
  };

  const handleSelectAllBranches = (checked) => {
    setForm((prev) => ({
      ...prev,
      branch_ids: checked ? [...allBranchIds] : [],
    }));
  };

  const handleProductRowChange = (index, key, value) => {
    setForm((prev) => {
      const nextRows = [...prev.products];
      const currentRow = { ...nextRows[index], [key]: value };

      if (Number(prev.offer_type) === 4 && key === "offer_item_id") {
        currentRow.free_item_id = value;
      }

      nextRows[index] = currentRow;
      return { ...prev, products: nextRows };
    });
  };

  const addRow = () => {
    setForm((prev) => ({
      ...prev,
      products: [...prev.products, createEmptyRow()],
    }));
  };

  const removeRow = (index) => {
    setForm((prev) => {
      const nextRows = prev.products.filter((_, rowIndex) => rowIndex !== index);
      return {
        ...prev,
        products: nextRows.length ? nextRows : [createEmptyRow()],
      };
    });
  };

  const onOfferTypeChange = (value) => {
    const offerType = Number(value);
    setForm((prev) => ({
      ...prev,
      offer_type: offerType,
      products: prev.products.map((row) => ({
        ...row,
        free_item_id: offerType === 4 ? row.offer_item_id : row.free_item_id,
      })),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      const payload = {
        ...form,
        offer_type: Number(form.offer_type),
        status: Number(form.status),
        branch_ids: form.branch_ids.map((value) => Number(value)),
        products: form.products.map((row) => ({
          offer_item_id: Number(row.offer_item_id),
          free_item_id:
            Number(form.offer_type) === 4
              ? Number(row.offer_item_id)
              : Number(row.free_item_id),
          offer_quantity: Number(row.offer_quantity),
          free_quantity: Number(row.free_quantity),
          max_qty: Number(row.max_qty || 0),
        })),
      };

      for (const [rowIndex, row] of payload.products.entries()) {
        if (!row.offer_item_id) {
          throw new Error(`Row ${rowIndex + 1}: Buy item is required`);
        }

        if (Number(payload.offer_type) === 5 && !row.free_item_id) {
          throw new Error(`Row ${rowIndex + 1}: Free item is required`);
        }

        if (Number(payload.offer_type) === 4 && Number(row.offer_item_id) !== Number(row.free_item_id)) {
          throw new Error(`Row ${rowIndex + 1}: Type 4 must use same item as free item`);
        }

        if (Number(payload.offer_type) === 5 && Number(row.offer_item_id) === Number(row.free_item_id)) {
          throw new Error(`Row ${rowIndex + 1}: Type 5 must use different free item`);
        }
      }

      const response = isEdit
        ? await offerAPI.updateManualOffer(id, payload)
        : await offerAPI.createManualOffer(payload);

      setSuccessMessage(
        response?.message || `Offer ${isEdit ? "updated" : "created"} successfully`
      );

      setTimeout(() => navigate("/admin/offers/bogo"), 800);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to save offer");
    } finally {
      setSaving(false);
    }
  };



  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      <DynamicBreadcrumb pathArray={breadcrumb} />

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {successMessage ? <Alert variant="success">{successMessage}</Alert> : null}

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{isEdit ? "Edit BOGO Offer" : "Create BOGO Offer"}</h5>
          <Link to="/admin/offers/bogo" className="btn btn-secondary btn-sm">
            Back
          </Link>
        </Card.Header>

        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Label>Offer Name</Form.Label>
                <Form.Control
                  value={form.offer_name}
                  onChange={(e) => handleFieldChange("offer_name", e.target.value)}
                  required
                />
              </Col>

              <Col md={3} className="mb-3">
                <Form.Label>Offer Type</Form.Label>
                <Form.Select
                  value={form.offer_type}
                  onChange={(e) => onOfferTypeChange(e.target.value)}
                >
                  <option value={4}>Type 4 - Same Product Free</option>
                  <option value={5}>Type 5 - Another Product Free</option>
                </Form.Select>
              </Col>

              <Col md={3} className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={form.status}
                  onChange={(e) => handleFieldChange("status", e.target.value)}
                >
                  <option value={1}>Active</option>
                  <option value={2}>Inactive</option>
                </Form.Select>
              </Col>

              <Col md={3} className="mb-3">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.offer_start_date}
                  onChange={(e) => handleFieldChange("offer_start_date", e.target.value)}
                  required
                />
              </Col>

              <Col md={3} className="mb-3">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.offer_end_date}
                  onChange={(e) => handleFieldChange("offer_end_date", e.target.value)}
                  required
                />
              </Col>
            </Row>

            <hr />
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Branches</h6>
              <Form.Check
                type="checkbox"
                id="select-all-branches"
                label="Select All Branches"
                checked={isAllBranchesSelected}
                onChange={(e) => handleSelectAllBranches(e.target.checked)}
              />
            </div>

            <Row>
              {branches.map((branch) => (
                <Col md={3} key={branch.branchId} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id={`branch-${branch.branchId}`}
                    label={branch.branchName}
                    checked={form.branch_ids.includes(Number(branch.branchId))}
                    onChange={(e) => handleBranchChange(branch.branchId, e.target.checked)}
                  />
                </Col>
              ))}
            </Row>

            <hr />
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Offer Product Mappings</h6>
              <Button variant="outline-primary" size="sm" onClick={addRow}>
                Add Row
              </Button>
            </div>

            {form.products.map((row, index) => (
              <Card key={index} className="mb-3 border">
                <Card.Body>
                  <Row>
                    <Col md={4} className="mb-3">
                      <Form.Label>Buy Item</Form.Label>
                      <ProductItemSearchSelect
                        value={row.offer_item_id}
                        onChange={(selectedValue) =>
                          handleProductRowChange(index, "offer_item_id", selectedValue)
                        }
                        placeholder="Type ref code or product name"
                      />
                    </Col>

                    <Col md={4} className="mb-3">
                      <Form.Label>Free Item</Form.Label>
                      <ProductItemSearchSelect
                        value={Number(form.offer_type) === 4 ? row.offer_item_id : row.free_item_id}
                        onChange={(selectedValue) =>
                          handleProductRowChange(index, "free_item_id", selectedValue)
                        }
                        placeholder="Type ref code or product name"
                        isDisabled={Number(form.offer_type) === 4}
                      />
                    </Col>

                    <Col md={1} className="mb-3">
                      <Form.Label>Buy Qty</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        value={row.offer_quantity}
                        onChange={(e) =>
                          handleProductRowChange(index, "offer_quantity", e.target.value)
                        }
                        required
                      />
                    </Col>

                    <Col md={1} className="mb-3">
                      <Form.Label>Free Qty</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        value={row.free_quantity}
                        onChange={(e) =>
                          handleProductRowChange(index, "free_quantity", e.target.value)
                        }
                        required
                      />
                    </Col>

                    <Col md={1} className="mb-3">
                      <Form.Label>Max Qty</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        value={row.max_qty}
                        onChange={(e) =>
                          handleProductRowChange(index, "max_qty", e.target.value)
                        }
                      />
                    </Col>

                    <Col md={1} className="mb-3 d-flex align-items-end">
                      <Button variant="outline-danger" onClick={() => removeRow(index)}>
                        Remove
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}

            <div className="d-flex justify-content-end gap-2">
              <Link to="/admin/offers/bogo" className="btn btn-secondary">
                Cancel
              </Link>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : isEdit ? "Update Offer" : "Create Offer"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default OfferManualForm;